/**
 * buildForecast.js
 *
 * Pure client-side projection utility.
 * No Firestore reads. No Firestore writes.
 * Every output item has `isCertain: false` — it is NEVER an actual job record.
 *
 * Projection logic:
 *   Anchor priority: nextJobDate → lastJobDate + interval → startDate → today
 *   Past anchors are fast-forwarded by interval multiples until >= today.
 *   Each plan produces MIN_PER_PLAN…MAX_PER_PLAN projected visits.
 */

const DEFAULT_HORIZON_DAYS = 60;
const MIN_PER_PLAN          = 3;
const MAX_PER_PLAN          = 12;

/**
 * Statuses that represent committed-but-not-yet-finished work. Mirrors the
 * Tier 2 idempotency widening in servicePlanSeeding.js — keep the two lists
 * in sync so the seeder and the read UI agree on what counts as "an active
 * task already exists".
 */
export const NON_TERMINAL_RECURRING_STATUSES = new Set([
  'approved',
  'scheduled',
  'en_route',
  'on_site',
  'pending',
  'in_progress',
]);

const TERMINAL_RECURRING_STATUSES = new Set([
  'completed',
  'cancelled',
  'no_show',
  'no-show',
]);

/**
 * Task statuses that should be hidden from operational scheduling boards
 * (Calendar Day/Week/Month + Dispatch + Build Day). These are visits the
 * operator (or a cascade like program-deactivate / account-close) explicitly
 * killed — they no longer represent committed work.
 *
 * `completed` is deliberately NOT in this set: a completed visit is shown
 * on the board as historical context (operator can confirm the work was
 * done). Only operator-killed terminal states drop off the board.
 */
export const BOARD_HIDDEN_STATUSES = new Set([
  'cancelled',
  'no_show',
  'no-show',
]);

/** True when a task should render on operational scheduling boards. */
export function isBoardVisibleTask(task) {
  if (!task) return false;
  return !BOARD_HIDDEN_STATUSES.has(task.status);
}

export function isNonTerminalRecurringTask(task) {
  if (!task || task.isRecurring !== true) return false;
  if (!task.scheduledStart) return false;
  const status = task.status || '';
  // Be inclusive — anything not explicitly terminal counts as committed.
  if (TERMINAL_RECURRING_STATUSES.has(status)) return false;
  return true;
}

// ── Date helpers ──────────────────────────────────────────────────────────────

/** Normalize any date value to a local-midnight JS Date. Exported so other
 *  components (e.g. ServicePlanCard) can share the timezone-safe parsing. */
export function toLocalDate(value) {
  if (!value) return null;
  if (value instanceof Date) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }
  const str = String(value);
  // "2026-05-01" — treat as local date (no timezone shift)
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    const [y, m, d] = str.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  // ISO datetime (UTC from backend) — convert to local date
  const dt = new Date(str);
  return isNaN(dt.getTime()) ? null : new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
}

function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

export function toDateString(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Returns an ISO week key like "2026-W20" for date grouping. */
export function isoWeekKey(dateOrStr) {
  const d = typeof dateOrStr === 'string'
    ? new Date(dateOrStr + 'T00:00:00')
    : new Date(dateOrStr);
  d.setHours(0, 0, 0, 0);
  // Thursday of the same week (ISO week definition)
  const thursday = new Date(d);
  thursday.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  const week1 = new Date(thursday.getFullYear(), 0, 4);
  const weekNum = 1 + Math.round(
    ((thursday.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7
  );
  return `${thursday.getFullYear()}-W${String(weekNum).padStart(2, '0')}`;
}

/**
 * Classify a scheduled task by how stale its date is (read-only label).
 *
 *  diffDays  <  1  → null    (today/future — show as normal "Scheduled")
 *  diffDays === 1  → 'late_today'
 *  2 ≤ diffDays ≤ 7 → 'overdue'
 *  diffDays >  7   → 'stale'
 *
 * @param {string|Date} scheduledStart
 * @returns {null | 'late_today' | 'overdue' | 'stale'}
 */
export function classifyScheduledStaleness(scheduledStart) {
  const start = toLocalDate(scheduledStart);
  if (!start) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.floor((today.getTime() - start.getTime()) / 86400000);
  if (diffDays < 1) return null;
  if (diffDays === 1) return 'late_today';
  if (diffDays <= 7)  return 'overdue';
  return 'stale';
}

/**
 * Return the earliest non-terminal recurring task for a given lead, or null.
 * Used by ServicePlanCard to prefer the live calendar truth over the cached
 * `recurrence.nextJobDate` cursor.
 */
export function findEarliestLiveRecurringTask(leadId, tasks) {
  if (!leadId || !tasks?.length) return null;
  const candidates = tasks.filter(
    (t) => t.leadId === leadId && isNonTerminalRecurringTask(t)
  );
  if (!candidates.length) return null;
  candidates.sort((a, b) =>
    String(a.scheduledStart).localeCompare(String(b.scheduledStart))
  );
  return candidates[0];
}

/**
 * Multi-Program Phase 2 restore — enrollment-scoped variant.
 *
 * Returns the earliest non-terminal recurring task belonging to a
 * specific enrollment. Rules (matching the seat/reschedule scoping
 * used by the backend Phase 1b engine):
 *   1. Prefer explicit enrollment tagging: t.enrollmentId === eid
 *   2. Fall back to legacy series identity: t.recurringServiceId === eid
 *   3. Legacy safety net — only when this enrollment IS the lead's
 *      primary (activeEnrollmentId points at it): allow tasks where
 *      t.leadId === leadId AND neither modern identifier is present.
 *      This prevents the Reschedule button on Enrollment B from
 *      accidentally selecting a legacy visit that actually belongs
 *      to the primary Enrollment A.
 */
export function findEarliestLiveRecurringTaskForEnrollment(
  enrollment, tasks, { leadId, activeEnrollmentId } = {},
) {
  if (!enrollment?.enrollmentId || !tasks?.length) return null;
  const eid = enrollment.enrollmentId;
  const isPrimary = activeEnrollmentId === eid;
  const candidates = tasks.filter((t) => {
    if (!isNonTerminalRecurringTask(t)) return false;
    if (t.enrollmentId === eid) return true;
    if (t.recurringServiceId === eid) return true;
    // Legacy fallback — only for the primary enrollment.
    if (isPrimary && leadId && t.leadId === leadId
        && !t.enrollmentId && !t.recurringServiceId) {
      return true;
    }
    // Very-legacy: recurringServiceId was the leadId in the pre-Phase-1b
    // engine. Only accept those for the primary enrollment.
    if (isPrimary && leadId && t.recurringServiceId === leadId) return true;
    return false;
  });
  if (!candidates.length) return null;
  candidates.sort((a, b) =>
    String(a.scheduledStart).localeCompare(String(b.scheduledStart))
  );
  return candidates[0];
}

/** Returns the Monday of the ISO week containing `date`. */
function weekMonday(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const dayOfWeek = (d.getDay() + 6) % 7; // 0=Mon, 6=Sun
  d.setDate(d.getDate() - dayOfWeek);
  return d;
}

// ── Core projection ────────────────────────────────────────────────────────────

/**
 * Project future visit dates for a single lead's active recurrence plan.
 * Returns an array of ProjectedVisit objects — never written to Firestore.
 */
function projectPlan(lead, horizonEnd) {
  const rec = lead.recurrence;
  if (!rec?.active) return [];

  const intervalDays = parseInt(rec.interval, 10);
  if (!intervalDays || intervalDays <= 0) return [];

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const completedOccurrences = parseInt(rec.completedOccurrences, 10) || 0;
  const totalOccurrences     = rec.totalOccurrences != null
    ? parseInt(rec.totalOccurrences, 10)
    : null;

  // Already at or past the plan limit — nothing to project
  if (totalOccurrences !== null && completedOccurrences >= totalOccurrences) return [];

  // ── Anchor selection (priority order) ──────────────────────────────────────
  let anchor =
    toLocalDate(rec.nextJobDate) ||
    (rec.lastJobDate ? addDays(toLocalDate(rec.lastJobDate), intervalDays) : null) ||
    toLocalDate(rec.startDate) ||
    today;

  // ── Fast-forward past anchors until >= today ────────────────────────────────
  let skippedCycles = 0;
  while (anchor < today) {
    anchor = addDays(anchor, intervalDays);
    skippedCycles += 1;
  }

  // ── Generate projection series ──────────────────────────────────────────────
  const projections = [];
  let currentDate  = anchor;
  let visitIndex   = completedOccurrences + 1 + skippedCycles;

  while (projections.length < MAX_PER_PLAN) {
    // Hard stop at plan's total
    if (totalOccurrences !== null && visitIndex > totalOccurrences) break;

    // Respect horizon, but always emit at least MIN_PER_PLAN
    if (currentDate > horizonEnd && projections.length >= MIN_PER_PLAN) break;

    projections.push({
      leadId:           lead.id,
      clientName:       [lead.firstName, lead.lastName].filter(Boolean).join(' ') ||
                        lead.name ||
                        'Unknown Client',
      serviceLabel:     rec.planLabel || 'Recurring Service',
      projectedDate:    toDateString(currentDate),
      visitIndex,
      totalOccurrences,
      remainingAfter:   totalOccurrences !== null ? totalOccurrences - visitIndex : null,
      intervalDays,
      isCertain:        false,   // ← ALWAYS false — projected only, never an actual job
      assignedTo:       rec.assignedTo || null,
    });

    currentDate = addDays(currentDate, intervalDays);
    visitIndex  += 1;
  }

  return projections;
}

// ── Deduplication against real tasks ──────────────────────────────────────────

/**
 * Cross-reference projections against all real tasks (any status except completed)
 * for the same lead.  If a task exists within ±3 days of the projected date, the
 * row is NOT suppressed — instead it is marked `isScheduled: true` so the UI can
 * render it as "Scheduled ✓" rather than "Projected".
 *
 * Intentionally checks ALL tasks (not just isRecurring) because a manually
 * scheduled task is equally valid evidence that the projected visit is covered.
 *
 * Uses the in-memory tasks array from TasksContext — NO Firestore reads.
 */
function deduplicateAgainstRealJobs(projections, tasks) {
  if (!tasks?.length) return projections;

  // Index ALL non-completed tasks by leadId for O(1) lookup
  const realByLead = new Map();
  for (const task of tasks) {
    if (!task.leadId || task.status === 'completed') continue;
    if (!realByLead.has(task.leadId)) realByLead.set(task.leadId, []);
    realByLead.get(task.leadId).push(task);
  }

  return projections.map((proj) => {
    const realTasks = realByLead.get(proj.leadId);
    if (!realTasks?.length) return proj; // pure projection — no real job found

    const projTime = new Date(proj.projectedDate + 'T00:00:00').getTime();

    // Find the nearest real task within ±3 days
    const matchedTask = realTasks.find((task) => {
      if (!task.scheduledStart) return false;
      const taskTime = toLocalDate(task.scheduledStart)?.getTime();
      if (!taskTime) return false;
      return Math.abs(projTime - taskTime) <= 3 * 86400 * 1000;
    });

    if (!matchedTask) return proj; // pure projection

    // Mark as "Scheduled" — the row stays in the list but renders differently
    return {
      ...proj,
      isScheduled:   true,
      scheduledDate: matchedTask.scheduledStart
        ? toDateString(toLocalDate(matchedTask.scheduledStart))
        : null,
      scheduledStatus: matchedTask.status || 'approved',
    };
  });
}

// ── Public API ─────────────────────────────────────────────────────────────────

/**
 * buildForecast
 *
 * Pure function. No Firestore reads or writes.
 * Accepts the in-memory leads array from LeadsContext and the real-time tasks
 * array from TasksContext (for deduplication only).
 *
 * @param {Array}  leads       - All leads from LeadsContext
 * @param {Array}  tasks       - All tasks from TasksContext (for deduplication)
 * @param {number} horizonDays - Days into future to project (default 60)
 * @returns {ProjectedVisit[]} - Sorted by projectedDate ascending
 */
export function buildForecast(leads, tasks, horizonDays = DEFAULT_HORIZON_DAYS) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const horizonEnd = addDays(today, horizonDays);

  const all = [];
  for (const lead of leads) {
    if (!lead.recurrence?.active) continue;
    all.push(...projectPlan(lead, horizonEnd));
  }

  // Sort by date ascending before deduplication
  all.sort((a, b) => a.projectedDate.localeCompare(b.projectedDate));

  return deduplicateAgainstRealJobs(all, tasks);
}

/**
 * groupByWeek
 *
 * Groups ProjectedVisit[] by ISO week.
 * Returns an ordered array of { weekKey, weekLabel, monday, visits }.
 */
export function groupByWeek(projections) {
  const map = new Map();
  for (const visit of projections) {
    const date   = new Date(visit.projectedDate + 'T00:00:00');
    const key    = isoWeekKey(date);
    if (!map.has(key)) {
      const monday = weekMonday(date);
      const sunday = addDays(monday, 6);
      map.set(key, {
        weekKey:   key,
        weekLabel: `${monday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${sunday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`,
        monday:    toDateString(monday),
        visits:    [],
      });
    }
    map.get(key).visits.push(visit);
  }
  return Array.from(map.values());
}

/** Compute a simple per-week visit count for the workload summary widget. */
export function weeklyWorkloadSummary(projections) {
  const weeks = groupByWeek(projections);
  if (!weeks.length) return [];
  const max = Math.max(...weeks.map((w) => w.visits.length), 1);
  return weeks.map((w) => ({
    ...w,
    count:    w.visits.length,
    barPct:   Math.round((w.visits.length / max) * 100),
  }));
}

export const HORIZON_OPTIONS = [
  { label: '2 weeks',  days: 14 },
  { label: '1 month',  days: 30 },
  { label: '2 months', days: 60 },
  { label: '3 months', days: 90 },
];

// ── Per-lead Service Outlook (Option A) ───────────────────────────────────────
//
// Pure read-only projection for ONE lead, used by the Service Outlook card on
// the client profile. NO Firestore reads or writes. NO mutation of recurrence
// or task state. Drift is informational only.

/**
 * Compute the season-level summary + projections + drift for a single lead.
 *
 * Behavior (corrected per operator-facing requirements):
 *   1. Collect non-terminal recurring tasks (anything not completed/cancelled).
 *   2. Dedupe scheduled rows by `seriesIndex` (falling back to scheduled-day
 *      bucket for legacy tasks missing `seriesIndex`). Duplicates are surfaced
 *      via `duplicates` so the UI can render a warning row.
 *   3. Classify past-dated scheduled rows as `late_today` / `overdue` / `stale`.
 *   4. Continue projection visit numbers AFTER the max real scheduled visit.
 *   5. For finite plans (totalOccurrences != null) project the FULL remaining
 *      season so summary count, list length, and expander label always agree.
 *   6. For unlimited plans, keep MAX_PER_PLAN cap to avoid infinite scrolling.
 *   7. Surface `cursorMismatch` when `recurrence.nextJobDate` and the earliest
 *      live task disagree by more than 1 day. UI renders an explanatory caption.
 *
 * Pure read-only — no Firestore reads or writes; no recurrence mutation.
 *
 * @param {object} lead         - Lead with `.recurrence` and `.id`
 * @param {Array}  tasks        - All tasks from TasksContext (real scheduled rows)
 * @param {number} horizonDays  - Days of projection horizon for UNLIMITED plans (default 180)
 * @returns {{
 *   projections: Array,
 *   summary: { completed, total, remaining, estimatedSeasonEnd, nextDate, intervalDays, planLabel } | null,
 *   drift:   { driftDays, severity } | null,
 *   duplicates: Array<{ visitIndex, count, taskIds }>,
 *   overdueCount: number,
 *   staleCount:   number,
 *   cursorMismatch: { liveDate: string, cursorDate: string, deltaDays: number } | null,
 *   liveNextTask:   object | null,
 *   isActive: boolean
 * }}
 */
export function projectPlanWithDrift(lead, tasks, horizonDays = 180) {
  const rec = lead?.recurrence;
  if (!rec?.active) {
    return {
      projections: [], summary: null, drift: null,
      duplicates: [], overdueCount: 0, staleCount: 0,
      cursorMismatch: null, liveNextTask: null, isActive: false,
    };
  }

  const intervalDays = parseInt(rec.interval, 10);
  if (!intervalDays || intervalDays <= 0) {
    return {
      projections: [], summary: null, drift: null,
      duplicates: [], overdueCount: 0, staleCount: 0,
      cursorMismatch: null, liveNextTask: null, isActive: false,
    };
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const completed = parseInt(rec.completedOccurrences, 10) || 0;
  const total     = rec.totalOccurrences != null ? parseInt(rec.totalOccurrences, 10) : null;
  const planLabel = rec.planLabel || 'Recurring Service';
  const clientName = [lead.firstName, lead.lastName].filter(Boolean).join(' ')
    || lead.name || 'Unknown Client';

  // Plan already exhausted — nothing to show.
  if (total != null && completed >= total) {
    return {
      projections: [],
      summary: { completed, total, remaining: 0, estimatedSeasonEnd: null,
                 nextDate: null, intervalDays, planLabel },
      drift: null,
      duplicates: [], overdueCount: 0, staleCount: 0,
      cursorMismatch: null, liveNextTask: null,
      isActive: true,
    };
  }

  // ── 1. Collect real, non-terminal recurring tasks (scheduled rows) ─────────
  const realScheduled = (tasks || [])
    .filter((t) => t.leadId === lead.id && isNonTerminalRecurringTask(t))
    .sort((a, b) => String(a.scheduledStart).localeCompare(String(b.scheduledStart)));

  // ── 2. Dedupe by seriesIndex (or by scheduled-day if seriesIndex missing) ─
  // Surface duplicates explicitly via `duplicates` so the UI shows a warning.
  const groups = new Map(); // key → array of raw task docs
  for (const t of realScheduled) {
    const explicitIndex = parseInt(t.seriesIndex || t.visitNumber, 10);
    const dateRaw = toLocalDate(t.scheduledStart);
    const dateKey = dateRaw ? toDateString(dateRaw) : 'no-date';
    const key = Number.isFinite(explicitIndex) && explicitIndex > 0
      ? `idx:${explicitIndex}`
      : `date:${dateKey}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(t);
  }

  const duplicates = []; // [{ visitIndex, count, taskIds }]
  const dedupedScheduled = [];
  for (const [key, group] of groups) {
    // Pick the most recently updated/created as the canonical representative,
    // but never drop information about duplicates — surface them to the UI.
    const sorted = group.slice().sort((a, b) => {
      const au = String(a.updatedAt || a.createdAt || '');
      const bu = String(b.updatedAt || b.createdAt || '');
      return bu.localeCompare(au); // newest first
    });
    const primary = sorted[0];
    if (group.length > 1) {
      const idxFromKey = key.startsWith('idx:') ? parseInt(key.slice(4), 10) : null;
      duplicates.push({
        visitIndex: idxFromKey,
        dateKey:    key.startsWith('date:') ? key.slice(5) : null,
        count:      group.length,
        taskIds:    group.map((t) => t.id).filter(Boolean),
      });
    }
    dedupedScheduled.push(primary);
  }
  // Re-sort by scheduledStart after dedupe so timeline ordering is preserved.
  dedupedScheduled.sort((a, b) =>
    String(a.scheduledStart).localeCompare(String(b.scheduledStart))
  );

  // ── 3. Build scheduled-row view-models with overdue classification ────────
  let overdueCount = 0;
  let staleCount   = 0;
  const scheduledRows = dedupedScheduled.map((t, i) => {
    const explicitIndex = parseInt(t.seriesIndex || t.visitNumber, 10);
    const visitIndex = Number.isFinite(explicitIndex) && explicitIndex > 0
      ? explicitIndex
      : (completed + i + 1);
    const date = toLocalDate(t.scheduledStart);
    const staleness = classifyScheduledStaleness(t.scheduledStart);
    if (staleness === 'overdue' || staleness === 'late_today') overdueCount += 1;
    if (staleness === 'stale') staleCount += 1;
    return {
      leadId: lead.id,
      clientName,
      serviceLabel: planLabel,
      projectedDate: date ? toDateString(date) : null,
      scheduledDate: date ? toDateString(date) : null,
      scheduledStatus: t.status || 'approved',
      staleness, // null | 'late_today' | 'overdue' | 'stale'
      visitIndex,
      totalOccurrences: total,
      remainingAfter: total != null ? total - visitIndex : null,
      intervalDays,
      isCertain: true,
      isScheduled: true,
      taskId: t.id || null,
      assignedTo: t.assignedTo || rec.assignedTo || null,
    };
  });

  // ── 4. Determine projection floor (visit index) and anchor (date) ─────────
  const maxScheduledVisit = scheduledRows.length > 0
    ? Math.max(...scheduledRows.map((r) => r.visitIndex))
    : completed;

  const lastScheduledDate = scheduledRows.length > 0
    ? toLocalDate(scheduledRows[scheduledRows.length - 1].scheduledDate)
    : null;

  let anchor;
  if (lastScheduledDate) {
    anchor = addDays(lastScheduledDate, intervalDays);
  } else {
    anchor =
      toLocalDate(rec.nextJobDate) ||
      (rec.lastJobDate ? addDays(toLocalDate(rec.lastJobDate), intervalDays) : null) ||
      toLocalDate(rec.startDate) ||
      today;
    while (anchor < today) {
      anchor = addDays(anchor, intervalDays);
    }
  }

  // ── 5. Project remaining visits ───────────────────────────────────────────
  const projectionCap = total != null
    ? Math.max(0, total - maxScheduledVisit)
    : MAX_PER_PLAN;

  const horizonEnd = addDays(today, horizonDays);

  const projectionRows = [];
  let currentDate = anchor;
  let visitIndex  = maxScheduledVisit + 1;

  while (projectionRows.length < projectionCap) {
    if (total != null && visitIndex > total) break;
    if (total == null && currentDate > horizonEnd && projectionRows.length >= 3) break;

    projectionRows.push({
      leadId: lead.id,
      clientName,
      serviceLabel: planLabel,
      projectedDate: toDateString(currentDate),
      visitIndex,
      totalOccurrences: total,
      remainingAfter: total != null ? total - visitIndex : null,
      intervalDays,
      isCertain: false,
      isScheduled: false,
      staleness: null,
      assignedTo: rec.assignedTo || null,
    });

    currentDate = addDays(currentDate, intervalDays);
    visitIndex += 1;
  }

  const projections = [...scheduledRows, ...projectionRows];

  // ── 6. Live next task + cursor-mismatch detection ─────────────────────────
  const liveNextTask = scheduledRows.length > 0 ? scheduledRows[0] : null;
  let cursorMismatch = null;
  if (liveNextTask && rec.nextJobDate) {
    const liveDate   = toLocalDate(liveNextTask.scheduledDate);
    const cursorDate = toLocalDate(rec.nextJobDate);
    if (liveDate && cursorDate) {
      const deltaDays = Math.round((liveDate - cursorDate) / 86400000);
      if (Math.abs(deltaDays) > 1) {
        cursorMismatch = {
          liveDate:   toDateString(liveDate),
          cursorDate: toDateString(cursorDate),
          deltaDays,
        };
      }
    }
  }

  // ── 7. Summary ────────────────────────────────────────────────────────────
  const nextDate = projections[0]?.projectedDate || null;
  const remaining = total != null ? Math.max(0, total - completed) : null;
  const estimatedSeasonEnd = (total != null && projections.length > 0)
    ? projections[projections.length - 1].projectedDate
    : null;

  const summary = {
    completed,
    total,
    remaining,
    estimatedSeasonEnd,
    nextDate,
    intervalDays,
    planLabel,
  };

  // ── 8. Drift ──────────────────────────────────────────────────────────────
  let drift = null;
  if (completed > 0 && projectionRows.length > 0) {
    const startAnchor = toLocalDate(rec.startDate) || toLocalDate(rec.lastJobDate);
    if (startAnchor) {
      const idealNext = addDays(startAnchor, (maxScheduledVisit + 1) * intervalDays);
      const actualNext = toLocalDate(projectionRows[0].projectedDate);
      if (actualNext) {
        const driftDays = Math.round((actualNext - idealNext) / 86400000);
        const threshold = Math.max(7, Math.floor(intervalDays * 0.3));
        if (driftDays >= threshold) {
          drift = {
            driftDays,
            severity: driftDays >= intervalDays ? 'high' : 'medium',
          };
        }
      }
    }
  }

  return {
    projections,
    summary,
    drift,
    duplicates,
    overdueCount,
    staleCount,
    cursorMismatch,
    liveNextTask,
    isActive: true,
  };
}
